import { useNavigate } from "react-router-dom";
import { useMemo, useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_API_BASE;
const REFRESH_STALE_AFTER_MS = 30 * 60 * 1000;

function formatDateTime(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString();
}

function formatDuration(value) {
  const ms = Number(value);
  if (!Number.isFinite(ms) || ms <= 0) return "Not available";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} sec`;
  return `${Math.floor(seconds / 60)} min ${Math.round(seconds % 60)} sec`;
}

function number(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  return numeric.toLocaleString();
}

function isStaleStartedStatus(status) {
  if (status?.status !== "STARTED" || !status?.startedAtUtc) return false;
  const started = new Date(status.startedAtUtc).getTime();
  return Number.isFinite(started) && Date.now() - started > REFRESH_STALE_AFTER_MS;
}

export default function StaffHome() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("staffToken"), []);
  const [snapshotStatus, setSnapshotStatus] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Redirect to login if not logged in
  useEffect(() => {
    if (!token) navigate("/staff/login", { replace: true });
  }, [token, navigate]);

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const loadSnapshotStatus = useCallback(async () => {
    if (!token) return null;
    const res = await fetch(`${API}/staff/insights/best-deals/refresh/status`, {
      headers: authHeaders,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    setSnapshotStatus(data);
    setRefreshing(data?.status === "STARTED" && !isStaleStartedStatus(data));
    return data;
  }, [authHeaders, token]);

  useEffect(() => {
    let cancelled = false;
    setSnapshotLoading(true);
    loadSnapshotStatus()
      .catch(() => {
        if (!cancelled) setError("Unable to load Insights snapshot status.");
      })
      .finally(() => {
        if (!cancelled) setSnapshotLoading(false);
      });
    return () => { cancelled = true; };
  }, [loadSnapshotStatus]);

  useEffect(() => {
    if (snapshotStatus?.status !== "STARTED") return undefined;
    const id = window.setInterval(() => {
      loadSnapshotStatus().catch(() => setError("Unable to refresh Insights snapshot status."));
    }, 4000);
    return () => window.clearInterval(id);
  }, [loadSnapshotStatus, snapshotStatus?.status]);

  const logout = () => {
    localStorage.removeItem("staffToken");
    navigate("/staff/login", { replace: true });
  };

  const startRefresh = async () => {
    if (refreshing) return;
    const confirmed = window.confirm(
      "Refresh the Insights snapshot now? The current snapshot will remain live until the new refresh completes."
    );
    if (!confirmed) return;

    setError("");
    setMessage("");
    setRefreshing(true);

    try {
      const res = await fetch(`${API}/staff/insights/best-deals/refresh`, {
        method: "POST",
        headers: authHeaders,
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 409) {
        setMessage(data?.message || "A refresh is already running.");
        await loadSnapshotStatus();
        return;
      }

      if (!res.ok) {
        throw new Error(data?.message || "Unable to start refresh.");
      }

      setMessage(data?.message || "Refresh running...");
      setSnapshotStatus((prev) => ({
        ...prev,
        latestRefreshRunId: data.refreshRunId,
        status: data.status,
        startedAtUtc: data.startedAtUtc,
        completedAtUtc: null,
      }));
    } catch (err) {
      setRefreshing(false);
      setError(err.message || "Unable to start refresh. The current snapshot remains live.");
    }
  };

  const staleStarted = isStaleStartedStatus(snapshotStatus);
  const isActiveRefresh = (refreshing || snapshotStatus?.status === "STARTED") && !staleStarted;

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Top header with inline logout */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Staff Dashboard</h1>
        {token && (
          <div className="flex gap-2">
            <button
              onClick={logout}
              className="h-10 px-4 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Logout
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
        {/* Centered menu */}
        <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 rounded-xl">
          <div className="grid gap-4 w-full max-w-md p-6">
            <button
              onClick={() => navigate("/staff/invite-dealer")}
              className="w-full p-6 rounded-xl border border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100 text-lg font-medium text-center"
            >
              Invite Dealer
            </button>

            <button
              onClick={() => navigate("/staff/dealers/contact-review")}
              className="w-full p-6 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-lg font-medium text-center"
            >
              Dealer Contact Review
            </button>

            {/* Example future items */}
            <button
              onClick={() => navigate("/staff/manage-staff")}
              className="w-full p-6 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-lg font-medium text-center"
            >
              Manage Staff
            </button>

            <button
              onClick={() => navigate("/staff/reports")}
              className="w-full p-6 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-lg font-medium text-center"
            >
              Reports
            </button>
          </div>
        </div>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Insights Snapshot</h2>
              <p className="mt-1 text-sm text-gray-600">
                Manually refresh the Price Position snapshot used by Insights.
              </p>
            </div>
            <button
              type="button"
              onClick={loadSnapshotStatus}
              className="h-9 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-700 hover:bg-gray-100"
              disabled={snapshotLoading}
            >
              Reload
            </button>
          </div>

          {snapshotLoading ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              Loading snapshot status...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Snapshot timestamp</div>
                  <div className="mt-1 font-medium text-gray-900">{formatDateTime(snapshotStatus?.latestSnapshotTimestampUtc)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Rows</div>
                  <div className="mt-1 font-medium text-gray-900">{number(snapshotStatus?.snapshotRowCount)}</div>
                </div>
                <div className="rounded-lg bg-emerald-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-emerald-700">Below Estimate</div>
                  <div className="mt-1 font-medium text-gray-900">{number(snapshotStatus?.belowCount)}</div>
                </div>
                <div className="rounded-lg bg-amber-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-amber-700">Near Estimate</div>
                  <div className="mt-1 font-medium text-gray-900">{number(snapshotStatus?.nearCount)}</div>
                </div>
                <div className="rounded-lg bg-rose-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-rose-700">Above Estimate</div>
                  <div className="mt-1 font-medium text-gray-900">{number(snapshotStatus?.aboveCount)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Latest status</div>
                  <div className="mt-1 font-medium text-gray-900">{snapshotStatus?.status || "Unknown"}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Latest run</div>
                  <div className="mt-1 font-medium text-gray-900">#{snapshotStatus?.latestRefreshRunId || "N/A"}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Last successful duration</div>
                  <div className="mt-1 font-medium text-gray-900">{formatDuration(snapshotStatus?.lastSuccessfulDurationMilliseconds)}</div>
                </div>
              </div>

              {message && (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                  {message}
                </div>
              )}

              {snapshotStatus?.status === "SUCCEEDED" && (
                <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  Latest refresh published {number(snapshotStatus?.publishedRowCount)} rows in {formatDuration(snapshotStatus?.durationMilliseconds)}.
                </div>
              )}

              {snapshotStatus?.status === "FAILED" && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {snapshotStatus?.errorMessage || "The refresh failed. The previous snapshot remains live."}
                </div>
              )}

              {staleStarted && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  This refresh appears abandoned. Reload status or start a new refresh to let the server recover the stale run.
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={startRefresh}
                disabled={isActiveRefresh}
                className={`mt-5 w-full rounded-lg px-4 py-3 text-sm font-semibold ${
                  isActiveRefresh
                    ? "cursor-not-allowed bg-gray-300 text-gray-600"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isActiveRefresh ? "Refresh running..." : "Refresh Insights Snapshot"}
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
